/**
 * Creative Specs (spec §36) and the Prompt Plan (spec §42).
 *
 * A Creative Spec is the provider-neutral instruction for ONE output: its
 * format, its creative mode, the template and snapshots it binds to, and the
 * resolved layers. Nothing here names a provider. The router (provider.ts)
 * turns a spec into a provider run; swapping providers changes no field of a
 * spec (spec §3, §96).
 *
 * The Prompt Plan is the other half of the provider boundary: UI code never
 * builds a provider prompt (spec §42). The planner assembles it from objective,
 * brand snapshot, style, auction truth, template layout, safe zones, source
 * metadata and the tenant's free-text notes, and the RESULT is persisted here -
 * the normalised brief, the resolved prompt, the negative constraints, the
 * reference assets and the opaque provider parameters - so a run is auditable
 * and reproducible.
 */

import { z } from 'zod';
import { CreativeFormatSchema } from './catalog.js';
import { VisualStrategySchema, SubjectPolicySchema } from './catalog.js';
import { TemplateLayerTypeSchema, TemplateBindingSchema } from './template.js';

export const MEDIA_TYPES = ['IMAGE', 'VIDEO', 'COPY'] as const;
export const MediaTypeSchema = z.enum(MEDIA_TYPES);
export type MediaType = z.infer<typeof MediaTypeSchema>;

// ---------------------------------------------------------------------------
// Prompt plan
// ---------------------------------------------------------------------------

export const PromptPlanSchema = z
  .object({
    /** The normalised, provider-agnostic description of the desired creative. */
    brief: z.string(),
    /** The resolved provider prompt. Persisted for audit; never authored in
     *  the UI. */
    prompt: z.string(),
    /** Hard negatives passed to the provider (from blocked keywords etc.). */
    negative: z.array(z.string()).optional(),
    /** Media ids used as reference/subject images. */
    referenceAssetIds: z.array(z.string().min(1)).optional(),
    /** Opaque provider parameters (seed, quality mode, ...). Kept as an opaque
     *  record so the domain does not grow a field per provider knob. */
    parameters: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
export type PromptPlan = z.infer<typeof PromptPlanSchema>;

// ---------------------------------------------------------------------------
// Resolved layers - what the "Brand Locked Elements" panel reads (mockup)
// ---------------------------------------------------------------------------

/** A layer after its binding has been resolved against the snapshots. `locked`
 *  layers are the deterministic truth the panel shows as Locked. */
export const CreativeLayerSchema = z
  .object({
    id: z.string().min(1),
    type: TemplateLayerTypeSchema,
    resolvedFrom: TemplateBindingSchema.optional(),
    /** The resolved text/url value for text-bearing layers, for validation and
     *  for the locked-fields display. */
    value: z.string().optional(),
    locked: z.boolean(),
  })
  .strict();
export type CreativeLayer = z.infer<typeof CreativeLayerSchema>;

// ---------------------------------------------------------------------------
// Creative spec (image / video)
// ---------------------------------------------------------------------------

export const CreativeOutputSchema = z
  .object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    fps: z.number().int().positive().optional(),
    durationMs: z.number().int().positive().optional(),
  })
  .strict();
export type CreativeOutput = z.infer<typeof CreativeOutputSchema>;

export const CreativeSpecSchema = z
  .object({
    id: z.string().min(1),
    campaignId: z.string().min(1),
    mediaType: z.enum(['IMAGE', 'VIDEO']),
    format: CreativeFormatSchema,
    /** How this output's visual is produced (ADR-56 cut 7). */
    visualStrategy: VisualStrategySchema,
    /** Whether the subject may be generated - carried onto the spec so the
     *  renderer and the generate step enforce the rule per output. */
    subjectPolicy: SubjectPolicySchema,
    /** The real lot this output features (a lot-feature family renders one
     *  creative PER lot, ADR-56 cut 9). Resolved against
     *  auctionTruth.selectedLots on every read, so the subject can never drift. */
    subjectLotId: z.string().min(1).optional(),
    templateId: z.string().min(1).optional(),
    templateVersion: z.number().int().positive().optional(),
    brandSnapshotId: z.string().min(1),
    auctionTruthSnapshotId: z.string().min(1),
    sourceAssetIds: z.array(z.string().min(1)),
    style: z.string(),
    promptPlan: PromptPlanSchema.optional(),
    layers: z.array(CreativeLayerSchema),
    output: CreativeOutputSchema,
  })
  .strict()
  .superRefine((spec, ctx) => {
    // The hard rule: AI may not generate the visual when a real subject is
    // required (a specific consignment horse must be real media).
    if (spec.visualStrategy === 'AI_CREATIVE' && spec.subjectPolicy === 'REAL_SUBJECT_REQUIRED') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'AI_CREATIVE is not allowed when a real subject is required', path: ['visualStrategy'] });
    }
    // A video spec must declare a duration.
    if (spec.mediaType === 'VIDEO' && spec.output.durationMs === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'a VIDEO spec requires output.durationMs', path: ['output', 'durationMs'] });
    }
  });
export type CreativeSpec = z.infer<typeof CreativeSpecSchema>;

// ---------------------------------------------------------------------------
// Copy spec (platform-aware, spec §18)
// ---------------------------------------------------------------------------

/** The distribution surface a piece of copy is written for. Copy is
 *  platform-aware: a caption is not a subject line. */
export const COPY_SURFACES = ['FACEBOOK', 'INSTAGRAM', 'TIKTOK', 'YOUTUBE', 'EMAIL', 'SMS'] as const;
export const CopySurfaceSchema = z.enum(COPY_SURFACES);
export type CopySurface = z.infer<typeof CopySurfaceSchema>;

export const CopyCreativeSpecSchema = z
  .object({
    id: z.string().min(1),
    campaignId: z.string().min(1),
    surface: CopySurfaceSchema,
    auctionTruthSnapshotId: z.string().min(1),
    promptPlan: PromptPlanSchema.optional(),
  })
  .strict();
export type CopyCreativeSpec = z.infer<typeof CopyCreativeSpecSchema>;

/** The generated copy fields. Which are present depends on the surface (an
 *  email has a subject; an SMS is one short line). All copy references the same
 *  AuctionTruthSnapshot as the creative (spec §18). */
export const GeneratedCopySchema = z
  .object({
    headline: z.string().optional(),
    caption: z.string().optional(),
    shortCaption: z.string().optional(),
    cta: z.string().optional(),
    hashtags: z.array(z.string()).optional(),
    altText: z.string().optional(),
    emailSubject: z.string().optional(),
    emailPreview: z.string().optional(),
    emailBody: z.string().optional(),
    sms: z.string().optional(),
  })
  .strict();
export type GeneratedCopy = z.infer<typeof GeneratedCopySchema>;
